// apps/web/src/features/crm/deals/components/DealsTable.tsx
//
// Presentation layer over `<DataTable<EngagementListRow>>` for the v4 Deals
// read-view (#2248). No `useQuery` here so tests drive it with rows directly
// (mirrors `EngagementListTable`). Renders ONLY the columns the projection
// provides for a deal — `dealCode` / client / date — dropping the legacy
// per-stage badge / manager / archived columns v4 has no data for (P-3: no
// micro-stages). Sort + pagination are SERVER-driven, so no header-sort and
// `pageSize` is set past the loaded count to disable client paging.
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { EngagementListRow } from '@magimp/shared';
import { DataTable, type Column } from '../../../../ui/index.js';
import { formatDateTime } from '../../../../i18n/index.js';

export interface DealsTableProps {
  rows: readonly EngagementListRow[];
  onSelect: (row: EngagementListRow) => void;
  emptyState?: ReactNode;
}

export function DealsTable({ rows, onSelect, emptyState }: DealsTableProps) {
  const { t } = useTranslation(['deals', 'common']);
  const columns: Column<EngagementListRow>[] = [
    {
      key: 'dealCode',
      label: t('deals:fields.publicCode'),
      render: (r) => r.dealCode ?? '—',
    },
    {
      key: 'client',
      label: t('deals:fields.client'),
      render: (r) => r.company ?? '—',
    },
    {
      key: 'updatedAt',
      label: t('deals:fields.updated'),
      align: 'right',
      render: (r) => (
        <span style={{ color: 'var(--fg-2)', fontSize: 12 }}>{formatDateTime(r.updatedAt)}</span>
      ),
    },
  ];
  return (
    <DataTable<EngagementListRow>
      columns={columns}
      rows={rows}
      onSelect={onSelect}
      emptyState={emptyState}
      getRowKey={(r) => r.id}
      // WHY: server cursor pagination owns paging — never fragment the loaded
      // set into client pages.
      pageSize={Math.max(rows.length, 1)}
    />
  );
}
